import { c as _c } from "react/compiler-runtime";
import Head from "next/head";
import type React from "react";
import { DEFAULT_OG_IMAGE_URL, DEFAULT_SITE_DESCRIPTION, SITE_HOSTNAME, truncateMetaDescription } from "#/shared/lib/seo";

/**
 * Props for {@link SiteSeoHead}: per-page title, description, canonical, and social preview.
 */
export type SiteSeoHeadProps = {
  /** Document `<title>`; also used for `og:title` and `twitter:title`. */
  title: string;
  /** Meta description and social descriptions; defaults to {@link DEFAULT_SITE_DESCRIPTION}. */
  description?: string;
  /** Absolute canonical URL; must match the preferred URL for this document (incl. locale prefix). */
  canonicalUrl: string;
  /** Open Graph / Twitter image URL; defaults to {@link DEFAULT_OG_IMAGE_URL}. */
  ogImageUrl?: string;
  /**
   * When true, emits `robots` so the page is not indexed.
   * Default combination is `noindex, follow` so crawlers may still follow links to public pages.
   */
  noindex?: boolean;
  /**
   * When `noindex` is true: if true, use `noindex, nofollow`; if false (default), use `noindex, follow`.
   */
  noFollowWhenNoindex?: boolean;
};

/**
 * Renders `next/head` tags for core SEO and social sharing (canonical, meta description,
 * Open Graph, Twitter Card). Use once per page instead of duplicating tags in `_document`.
 */
export const SiteSeoHead: React.FC<SiteSeoHeadProps> = t0 => {
  const $ = _c(47);
  const {
    title,
    description: t1,
    canonicalUrl,
    ogImageUrl: t2,
    noindex: t3,
    noFollowWhenNoindex: t4
  } = t0;
  const description = t1 === undefined ? DEFAULT_SITE_DESCRIPTION : t1;
  const ogImageUrl = t2 === undefined ? DEFAULT_OG_IMAGE_URL : t2;
  const noindex = t3 === undefined ? false : t3;
  const noFollowWhenNoindex = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== description) {
    t5 = truncateMetaDescription(description);
    $[0] = description;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const metaDescription = t5;
  const robotsWhenNoindex = noFollowWhenNoindex ? "noindex, nofollow" : "noindex, follow";
  let t6;
  if ($[2] !== title) {
    t6 = <title>{title}</title>;
    $[2] = title;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== metaDescription) {
    t7 = <meta name="description" content={metaDescription} />;
    $[4] = metaDescription;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== canonicalUrl) {
    t8 = <link rel="canonical" href={canonicalUrl} />;
    $[6] = canonicalUrl;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== noindex || $[9] !== robotsWhenNoindex) {
    t9 = noindex ? <meta name="robots" content={robotsWhenNoindex} /> : null;
    $[8] = noindex;
    $[9] = robotsWhenNoindex;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <meta property="og:site_name" content="dStruct" />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== title) {
    t11 = <meta property="og:title" content={title} />;
    $[12] = title;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <meta property="og:type" content="website" />;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== canonicalUrl) {
    t13 = <meta property="og:url" content={canonicalUrl} />;
    $[15] = canonicalUrl;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== metaDescription) {
    t14 = <meta property="og:description" content={metaDescription} />;
    $[17] = metaDescription;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== ogImageUrl) {
    t15 = <meta property="og:image" content={ogImageUrl} />;
    $[19] = ogImageUrl;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <meta property="og:image:width" content="1200" />;
    t17 = <meta property="og:image:height" content="630" />;
    t18 = <meta property="og:image:alt" content={"dStruct \u2014 LeetCode solution visualizer"} />;
    t19 = <meta name="twitter:card" content="summary_large_image" />;
    t20 = <meta name="twitter:domain" content={SITE_HOSTNAME} />;
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
  } else {
    t16 = $[21];
    t17 = $[22];
    t18 = $[23];
    t19 = $[24];
    t20 = $[25];
  }
  let t21;
  if ($[26] !== canonicalUrl) {
    t21 = <meta name="twitter:url" content={canonicalUrl} />;
    $[26] = canonicalUrl;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== title) {
    t22 = <meta name="twitter:title" content={title} />;
    $[28] = title;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== metaDescription) {
    t23 = <meta name="twitter:description" content={metaDescription} />;
    $[30] = metaDescription;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] !== ogImageUrl) {
    t24 = <meta name="twitter:image" content={ogImageUrl} />;
    $[32] = ogImageUrl;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== t11 || $[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t21 || $[39] !== t22 || $[40] !== t23 || $[41] !== t24 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t25 = <Head>{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}</Head>;
    $[34] = t11;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
    $[41] = t24;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  return t25;
};